import { Controller } from "@hotwired/stimulus";
import localBootstrap from "bootstrap/dist/js/bootstrap";
import { trackUxEvent, scanImpressions } from "../utils/ux_event_tracker";

const bootstrap = new Proxy({}, {
  get(target, prop) {
    const activeBootstrap = typeof window !== "undefined" && window.bootstrap ? window.bootstrap : localBootstrap;
    return activeBootstrap[prop];
  }
});

export default class extends Controller {
  static targets = [
    "orderQty", "itemPrice",
    "mealSearch", "topItemFilter", "houseSpecialFilter",
    "glutenFreeFilter", "vegetarianFilter", "veganFilter",
    "stickyHeader", "noResults", "searchClear", "cartbar",
    "categoryUnderline", "catChevronLeft", "catChevronRight",
    "sheetSearchInput", "sheetSearchResults", "sheetSearchCount", "sheetSearchEmpty"
  ];

  // `features` is OnlineOrders::LayoutFeatures resolved for this visitor's arm
  // (snake_case keys, e.g. this.featuresValue.sticky_cta).
  static values = { focusItemId: Number, features: Object };

  connect() {
    this.scrollLockUntil = 0;
    this.activeCategoryId = null;
    this.activeScheduleId = null;
    this.scrollTickPending = false;
    this.lastScrollY = window.pageYOffset;
    this.collapseMql = window.matchMedia("(max-width: 767.98px)");

    this.initHeaderHeight();
    this.initCartbarHeight();
    this.scrollListener = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.scrollListener, { passive: true });
    this.initDragScroll();
    this.initScrollFade();
    this.updateScrollSpy();
    this.bindScrollLockFix();
    this.initFlashes();
    this.initSheetScrollDrag();
    this.initModalScrollGuard();
    this.initSheetFade();

    // Restore any button locked before navigation (Turbolinks cache restore)
    // and again on bfcache restores, where connect() never re-runs.
    this.resetLoadingButtons();
    this.onPageShow = (event) => {
      if (event.persisted) this.resetLoadingButtons();
    };
    window.addEventListener("pageshow", this.onPageShow);

    this.focusRequestedItem();
  }

  disconnect() {
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }
    if (this.cartbarResizeObserver) {
      this.cartbarResizeObserver.disconnect();
    }
    document.documentElement.style.removeProperty("--oc-cartbar-height");
    // Both live on <html>, which Turbolinks never replaces. The checkout pages don't mount this
    // controller and have no sticky header, so a leftover value would offset their flash stack
    // by the height of the menu header they navigated away from.
    document.documentElement.style.removeProperty("--oc-sticky-header-height");
    clearTimeout(this.orderTypeReloadTimer);
    window.removeEventListener("scroll", this.scrollListener);
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
    }
    this.unbindScrollLockFix();
    this.destroyScrollFade();
    this.destroySheetScrollDrag();
    this.destroyModalScrollGuard();
    this.destroySheetFade();
    window.removeEventListener("pageshow", this.onPageShow);
    this.teardownFocusListeners();
    this.searchTrackTimers?.forEach((timer) => clearTimeout(timer));
  }

  // Smart-menu deep link (?item_id=): once the page's auto-open modals (discount
  // notice, delivery-type) have all been dismissed, scroll to the requested item's
  // card and open its modal. No-ops if the card isn't on the page (item filtered
  // out). We can't know up front which auto-open modals will appear or in what
  // order (the discount notice chains into the delivery-type modal), so instead of
  // racing a fixed delay we debounce on modal activity: every show/hide resets a
  // settle timer, and we only open once things have gone quiet with nothing shown.
  focusRequestedItem() {
    // Always consume the deep-link param (one-shot) so a reload never re-opens
    // the modal or re-flashes the unavailable notice — including the flash-only
    // case where the server rendered a notice but set no focus id.
    this.stripFocusParam();

    if (!this.hasFocusItemIdValue || !this.focusItemIdValue) return;

    this.onFocusModalActivity = () => {
      clearTimeout(this.focusSettleTimer);
      this.focusSettleTimer = setTimeout(() => {
        // Something is still open (or mid-chain); wait for the next hide to retry.
        if (document.querySelector(".modal.show")) return;
        this.openFocusItem();
      }, 400);
    };

    document.addEventListener("show.bs.modal", this.onFocusModalActivity);
    document.addEventListener("shown.bs.modal", this.onFocusModalActivity);
    document.addEventListener("hidden.bs.modal", this.onFocusModalActivity);

    // Kick off the initial settle window (covers the no-auto-modal case).
    this.onFocusModalActivity();
  }

  teardownFocusListeners() {
    clearTimeout(this.focusSettleTimer);
    if (this.onFocusModalActivity) {
      document.removeEventListener("show.bs.modal", this.onFocusModalActivity);
      document.removeEventListener("shown.bs.modal", this.onFocusModalActivity);
      document.removeEventListener("hidden.bs.modal", this.onFocusModalActivity);
      this.onFocusModalActivity = null;
    }
  }

  stripFocusParam() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("item_id")) return;
      url.searchParams.delete("item_id");
      window.history.replaceState(window.history.state, "", url.toString());
    } catch (_e) {
      // No-op if URL/history is unavailable.
    }
  }

  openFocusItem() {
    // One-shot: drop the listeners first so opening the item modal (its own
    // show/hide events) can't retrigger this.
    this.teardownFocusListeners();

    const card = document.querySelector(`[data-item-id="${this.focusItemIdValue}"]`);
    if (!card) return;

    const headerOffset = this.hasStickyHeaderTarget
      ? this.stickyHeaderTarget.offsetHeight + 12
      : 180;
    const offsetPosition = card.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });

    // Reuse the existing remote link → newModal flow to open the item modal.
    card.querySelector('a[data-remote="true"]')?.click();
  }

  resetLoadingButtons() {
    this.element.querySelectorAll(".is-loading").forEach((button) => {
      button.classList.remove("is-loading", "disabled");
    });
  }

  bindScrollLockFix() {
    // Bind the handler references once so add/removeEventListener stay symmetric
    // even if this is ever invoked more than once.
    if (!this.onScrollLockShow) {
      this.clearScrollLockStyles = this.clearScrollLockStyles.bind(this);
      this.onScrollLockShow = () => {
        queueMicrotask(() => this.clearScrollLockStyles());
      };
    }

    if (!this.boundDocumentModalEvents) {
      document.addEventListener("show.bs.modal", this.onScrollLockShow);
      document.addEventListener("shown.bs.modal", this.clearScrollLockStyles);
      document.addEventListener("hide.bs.modal", this.onScrollLockShow);
      document.addEventListener("hidden.bs.modal", this.clearScrollLockStyles);
      this.boundDocumentModalEvents = true;
    }

    this.bindOffcanvasElement();
  }

  bindOffcanvasElement() {
    if (this.offcanvasElement) {
      if (this.onScrollLockShow) {
        this.offcanvasElement.removeEventListener("show.bs.offcanvas", this.onScrollLockShow);
        this.offcanvasElement.removeEventListener("shown.bs.offcanvas", this.clearScrollLockStyles);
        this.offcanvasElement.removeEventListener("hide.bs.offcanvas", this.onScrollLockShow);
        this.offcanvasElement.removeEventListener("hidden.bs.offcanvas", this.clearScrollLockStyles);
      }
      if (this.onModalTouchMove) {
        this.offcanvasElement.removeEventListener("touchmove", this.onModalTouchMove);
      }
    }

    this.offcanvasElement = document.getElementById("offcanvasRight");
    if (!this.offcanvasElement) return;

    if (this.onScrollLockShow) {
      this.offcanvasElement.addEventListener("show.bs.offcanvas", this.onScrollLockShow);
      this.offcanvasElement.addEventListener("shown.bs.offcanvas", this.clearScrollLockStyles);
      this.offcanvasElement.addEventListener("hide.bs.offcanvas", this.onScrollLockShow);
      this.offcanvasElement.addEventListener("hidden.bs.offcanvas", this.clearScrollLockStyles);
    }
    if (this.onModalTouchMove) {
      this.offcanvasElement.addEventListener("touchmove", this.onModalTouchMove, { passive: false });
    }
  }

  unbindScrollLockFix() {
    if (!this.onScrollLockShow) return;

    if (this.boundDocumentModalEvents) {
      document.removeEventListener("show.bs.modal", this.onScrollLockShow);
      document.removeEventListener("shown.bs.modal", this.clearScrollLockStyles);
      document.removeEventListener("hide.bs.modal", this.onScrollLockShow);
      document.removeEventListener("hidden.bs.modal", this.clearScrollLockStyles);
      this.boundDocumentModalEvents = false;
    }

    if (this.offcanvasElement) {
      this.offcanvasElement.removeEventListener("show.bs.offcanvas", this.onScrollLockShow);
      this.offcanvasElement.removeEventListener("shown.bs.offcanvas", this.clearScrollLockStyles);
      this.offcanvasElement.removeEventListener("hide.bs.offcanvas", this.onScrollLockShow);
      this.offcanvasElement.removeEventListener("hidden.bs.offcanvas", this.clearScrollLockStyles);
    }
  }

  clearScrollLockStyles() {
    // Undo Bootstrap's scrollbar compensation (the iOS layout-jump source).
    // Desktop: also drop the body overflow lock — hiding the scrollbar leaves a
    // white gutter (scrollbar-gutter: stable) and background scroll behind a
    // modal is acceptable there. Mobile sheets keep the lock; the touchmove
    // guard (initModalScrollGuard) handles iOS scroll chaining.
    if (!this.isMobileSheet()) {
      document.body.style.removeProperty("overflow");
    }
    document.body.style.removeProperty("padding-right");

    document.querySelectorAll(".modal.show").forEach((modal) => {
      modal.style.setProperty("padding", "0");
    });

    document.querySelectorAll(".modal.show .modal-dialog").forEach((dialog) => {
      dialog.style.removeProperty("padding-right");
      dialog.style.removeProperty("padding-left");
      dialog.style.removeProperty("margin-right");
      dialog.style.setProperty("margin-left", "auto");
      dialog.style.setProperty("margin-right", "auto");
    });

    document.querySelectorAll(".offcanvas.show").forEach((offcanvas) => {
      offcanvas.style.removeProperty("padding-right");
    });

    document.querySelectorAll(
      ".sticky-top, .fixed-top, .fixed-bottom, .is-fixed, .oc-sticky-header, .oc-cartbar"
    ).forEach((element) => {
      element.style.removeProperty("padding-right");
      element.style.removeProperty("margin-right");
    });
  }

  initDragScroll() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active-drag");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active-drag");
    });

    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active-drag");
    });

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      slider.scrollLeft = scrollLeft - walk;
    });
  }

  initScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    this.scrollFadeListener = this.updateScrollFade.bind(this);
    slider.addEventListener("scroll", this.scrollFadeListener, { passive: true });
    window.addEventListener("resize", this.scrollFadeListener, { passive: true });

    // Use ResizeObserver to reliably detect when the slider's layout actually finishes rendering
    this.scrollFadeObserver = new ResizeObserver(() => {
      this.updateScrollFade();
    });
    this.scrollFadeObserver.observe(slider);
    
    // Initial check, plus a slight delay in case of font loading
    requestAnimationFrame(() => this.updateScrollFade());
    setTimeout(() => this.updateScrollFade(), 100);
  }

  destroyScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (slider && this.scrollFadeListener) {
      slider.removeEventListener("scroll", this.scrollFadeListener);
    }
    if (this.scrollFadeListener) {
      window.removeEventListener("resize", this.scrollFadeListener);
    }
    if (this.scrollFadeObserver) {
      this.scrollFadeObserver.disconnect();
    }
  }

  updateScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    const scrollLeft = slider.scrollLeft;
    const scrollWidth = slider.scrollWidth;
    const clientWidth = slider.clientWidth;

    // Tolerance to handle subpixel rendering
    const tolerance = 4;
    const isAtStart = scrollLeft <= tolerance;
    const isAtEnd = (scrollLeft + clientWidth) >= (scrollWidth - tolerance);
    const canScroll = scrollWidth > clientWidth;

    if (!canScroll) {
      slider.classList.remove("mask-left", "mask-right", "mask-both");
      slider.classList.add("mask-none");
    } else if (isAtStart) {
      slider.classList.remove("mask-left", "mask-both", "mask-none");
      slider.classList.add("mask-right");
    } else if (isAtEnd) {
      slider.classList.remove("mask-right", "mask-both", "mask-none");
      slider.classList.add("mask-left");
    } else {
      slider.classList.remove("mask-left", "mask-right", "mask-none");
      slider.classList.add("mask-both");
    }

    // Hide the chevrons entirely when every pill fits (nothing to scroll);
    // otherwise show them and disable the one at the corresponding end.
    if (this.hasCatChevronLeftTarget) {
      this.catChevronLeftTarget.classList.toggle("d-none", !canScroll);
      this.catChevronLeftTarget.toggleAttribute("disabled", isAtStart || !canScroll);
    }
    if (this.hasCatChevronRightTarget) {
      this.catChevronRightTarget.classList.toggle("d-none", !canScroll);
      this.catChevronRightTarget.toggleAttribute("disabled", isAtEnd || !canScroll);
    }

    // Refresh underline position in case layout metrics changed
    if (this.activeCategoryId) {
      const activePill = document.querySelector(`.oc-category-pill[data-category-id="${this.activeCategoryId}"]`);
      if (activePill) this.updateCategoryUnderline(activePill);
    }
  }

  // --- Bottom scroll-fade on sheet bodies (item modal + search sheet) ---
  // A subtle fade at the bottom signals there's more to scroll; it clears at
  // the end. `scroll` doesn't bubble, so this listens in the capture phase
  // instead of binding to bodies that come and go (the item modal's is
  // re-injected per open, the search sheet's rows re-render per keystroke).
  initSheetFade() {
    this.onSheetFadeScroll = (event) => {
      if (event.target?.classList?.contains("js-sheet-fade")) this.updateSheetFade(event.target);
    };
    this.onSheetFadeRefresh = () => this.refreshSheetFades();
    this.element.addEventListener("scroll", this.onSheetFadeScroll, true);
    // Sheet heights settle after the show transition; content swaps mid-show
    // (sheet result rendering) call updateSheetFade directly.
    document.addEventListener("shown.bs.modal", this.onSheetFadeRefresh);
    document.addEventListener("shown.bs.offcanvas", this.onSheetFadeRefresh);
    window.addEventListener("resize", this.onSheetFadeRefresh, { passive: true });
  }

  destroySheetFade() {
    if (!this.onSheetFadeScroll) return;
    this.element.removeEventListener("scroll", this.onSheetFadeScroll, true);
    document.removeEventListener("shown.bs.modal", this.onSheetFadeRefresh);
    document.removeEventListener("shown.bs.offcanvas", this.onSheetFadeRefresh);
    window.removeEventListener("resize", this.onSheetFadeRefresh);
  }

  updateSheetFade(el) {
    const more = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
    el.classList.toggle("oc-sheet-fade--on", more);
  }

  refreshSheetFades() {
    this.element.querySelectorAll(".js-sheet-fade").forEach((el) => this.updateSheetFade(el));
  }

  scrollCategoriesLeft() {
    const scroller = document.querySelector(".oc-categories-scroll");
    if (!scroller) return;
    scroller.scrollBy({ left: -(scroller.clientWidth * 0.7), behavior: "smooth" });
  }

  scrollCategoriesRight() {
    const scroller = document.querySelector(".oc-categories-scroll");
    if (!scroller) return;
    scroller.scrollBy({ left: scroller.clientWidth * 0.7, behavior: "smooth" });
  }

  initHeaderHeight() {
    if (!this.hasStickyHeaderTarget) return;

    let lastHeight = null;
    const updateHeight = () => {
      const height = this.stickyHeaderTarget.offsetHeight;
      if (height !== lastHeight) {
        lastHeight = height;
        document.documentElement.style.setProperty("--oc-sticky-header-height", `${height}px`);
      }
    };

    updateHeight();

    this.headerResizeObserver = new ResizeObserver(updateHeight);
    this.headerResizeObserver.observe(this.stickyHeaderTarget);

    this.resizeListener = updateHeight;
    window.addEventListener("resize", this.resizeListener, { passive: true });
  }

  initCartbarHeight() {
    // The fixed cart bar's height varies with the device safe-area inset, so we
    // measure it and expose it as --oc-cartbar-height for .oc-app-body to reserve
    // exactly the right bottom padding. When the bar is absent (desktop, or not
    // taking orders) the value is 0 so no space is wasted.
    if (!this.hasCartbarTarget) {
      document.documentElement.style.setProperty("--oc-cartbar-height", "0px");
      return;
    }

    let lastHeight = null;
    const updateHeight = () => {
      const height = this.cartbarTarget.offsetHeight;
      if (height !== lastHeight) {
        lastHeight = height;
        document.documentElement.style.setProperty("--oc-cartbar-height", `${height}px`);
      }
    };

    updateHeight();

    this.cartbarResizeObserver = new ResizeObserver(updateHeight);
    this.cartbarResizeObserver.observe(this.cartbarTarget);
  }

  handleScroll() {
    if (this.scrollTickPending) return;
    this.scrollTickPending = true;

    requestAnimationFrame(() => {
      this.scrollTickPending = false;
      this.updateCollapseHeader();
      this.updateScrollSpy();
    });
  }

  // Collapse the secondary nav rows (type toggles + search) on scroll-down so the
  // sticky header shrinks and the menu reclaims the space; restore on scroll-up.
  // Stability: direction hysteresis (collapse only when scrolling down past y>80,
  // expand on any upward move) can't oscillate; a bottom guard prevents collapsing
  // while pinned near the page bottom (shrinking the document there would make the
  // browser re-clamp the scroll and snap); scroll anchoring is disabled in CSS so
  // the height change animates cleanly instead of being counter-scrolled.
  updateCollapseHeader() {
    if (!this.hasStickyHeaderTarget) return;

    const currentY = window.pageYOffset;

    if (!this.collapseMql.matches) {
      this.stickyHeaderTarget.classList.remove("oc-header--collapsed");
      this.lastScrollY = currentY;
      return;
    }

    const delta = currentY - this.lastScrollY;

    // Ignore sub-pixel/jitter movements so resting fingers can't flip the state.
    if (Math.abs(delta) < 4) return;

    const collapsed = this.stickyHeaderTarget.classList.contains("oc-header--collapsed");
    const nearBottom =
      window.innerHeight + currentY >= document.documentElement.scrollHeight - 120;

    if (!collapsed && delta > 0 && currentY > 80 && !nearBottom) {
      this.stickyHeaderTarget.classList.add("oc-header--collapsed");
    } else if (collapsed && (delta < 0 || currentY <= 10)) {
      this.stickyHeaderTarget.classList.remove("oc-header--collapsed");
    }

    this.lastScrollY = currentY;
  }

  updateScrollSpy() {
    if (this.hasStickyHeaderTarget) {
      this.stickyHeaderTarget.classList.toggle("is-scrolled", window.pageYOffset > 0);
    }

    if (Date.now() < this.scrollLockUntil) return;

    const categories = document.querySelectorAll(".scroll-margin");
    if (categories.length === 0) return;

    if ((window.innerHeight + window.pageYOffset) >= document.documentElement.scrollHeight - 80) {
      const lastCategory = categories[categories.length - 1];
      this.setActiveCategory(lastCategory.id);
      return;
    }

    const referenceLine = this.hasStickyHeaderTarget
      ? this.stickyHeaderTarget.getBoundingClientRect().bottom + 8
      : 120;

    let activeCategory = null;
    let minDistance = Infinity;

    categories.forEach((category) => {
      const rect = category.getBoundingClientRect();
      if (rect.bottom <= referenceLine) return;

      const distance = Math.abs(rect.top - referenceLine);
      if (distance < minDistance) {
        minDistance = distance;
        activeCategory = category;
      }
    });

    if (activeCategory) {
      this.setActiveCategory(activeCategory.id);
    }
  }

  setActiveCategory(categoryId) {
    if (this.activeCategoryId === categoryId) return;
    this.activeCategoryId = categoryId;

    const allLinks = document.querySelectorAll(".oc-category-pill, .oc-modal-category-link, .oc-dropdown-category-link, .oc-sidebar-category-link");
    let activePill = null;
    let activeSidebarLink = null;

    allLinks.forEach((link) => {
      if (link.dataset.categoryId === categoryId) {
        link.classList.add("active");
        if (link.classList.contains("oc-category-pill")) {
          activePill = link;
        }
        if (link.classList.contains("oc-sidebar-category-link")) {
          activeSidebarLink = link;
        }
      } else {
        link.classList.remove("active");
      }
    });

    if (activePill) {
      this.scrollPillIntoViewIfNeeded(activePill);
      this.updateCategoryUnderline(activePill);
    }

    // Open the active schedule group BEFORE scrolling its sidebar link into
    // view — otherwise the link is still collapsed (0 height) and the scroll
    // math is computed against a hidden element.
    this.syncNavGroups(categoryId);

    if (activeSidebarLink) {
      this.scrollSidebarLinkIntoView(activeSidebarLink);
    }
  }

  // --- Schedule accordion: open the active category's schedule group as scroll-spy
  // follows the page. Groups the server opened up-front (data-auto-open) never
  // auto-collapse — only a header click (toggleNavGroup) closes them. The rest
  // behave as an accordion: expanded while their schedule is active, collapsed
  // again when scroll-spy moves on. ---
  syncNavGroups(categoryId) {
    const link = document.querySelector(
      `[data-category-id="${categoryId}"][data-schedule-id]`
    );
    const scheduleId = link && link.dataset.scheduleId;
    if (!scheduleId || scheduleId === this.activeScheduleId) return;
    this.activeScheduleId = scheduleId;

    document.querySelectorAll(".oc-nav-group").forEach((group) => {
      if (group.dataset.scheduleId === scheduleId) {
        group.classList.add("is-open");
      } else if (!group.hasAttribute("data-auto-open")) {
        group.classList.remove("is-open");
      }
    });
  }

  toggleNavGroup(event) {
    const group = event.currentTarget.closest(".oc-nav-group");
    if (!group) return;
    group.classList.toggle("is-open");
    // A manual collapse revokes the server's auto-open: the group joins normal
    // accordion behavior instead of scroll-spy pinning it open again forever.
    if (!group.classList.contains("is-open")) group.removeAttribute("data-auto-open");
  }

  // Keep the active sidebar link visible by scrolling the sidebar's OWN scroll
  // container — never `scrollIntoView`, which can scroll the whole window
  // vertically (a cause of the page jumping while the scrollspy runs).
  scrollSidebarLinkIntoView(link) {
    const sidebar = document.querySelector(".oc-menu-sidebar");
    if (!sidebar) return;
    const sidebarRect = sidebar.getBoundingClientRect();
    // The sticky search bar overlays the top of the scrollport, so the usable
    // viewport starts below it — measure against its bottom edge or links
    // "into view" end up hidden underneath it.
    const search = sidebar.querySelector(".oc-sidebar-search");
    const visibleTop = search ? search.getBoundingClientRect().bottom : sidebarRect.top;
    const linkRect = link.getBoundingClientRect();
    const padding = 8;
    let delta = 0;
    if (linkRect.top < visibleTop + padding) {
      delta = linkRect.top - visibleTop - padding;
    } else if (linkRect.bottom > sidebarRect.bottom - padding) {
      delta = linkRect.bottom - sidebarRect.bottom + padding;
    }
    if (delta === 0) return;

    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    sidebar.scrollTo({
      top: sidebar.scrollTop + delta,
      behavior: smooth ? "smooth" : "auto",
    });
  }

  updateCategoryUnderline(activePill) {
    if (!this.hasCategoryUnderlineTarget) return;

    // Position the absolute underline relative to the scroll container's content
    const left = activePill.offsetLeft;
    const width = activePill.offsetWidth;
    
    this.categoryUnderlineTarget.style.transform = `translateX(${left}px)`;
    this.categoryUnderlineTarget.style.width = `${width}px`;
  }

  // Centre the active pill within its OWN horizontal scroller via scrollLeft —
  // never `scrollIntoView({ inline: "center" })`, which on several engines also
  // nudges the page's vertical scroll (and competes with the user's scrolling).
  scrollPillIntoViewIfNeeded(pill) {
    const scroller = document.querySelector(".oc-categories-scroll");
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    const padding = 8;
    const isVisible =
      pillRect.left >= scrollerRect.left + padding &&
      pillRect.right <= scrollerRect.right - padding;

    if (!isVisible) {
      scroller.scrollTo({
        left: Math.max(0, pill.offsetLeft + pill.offsetWidth / 2 - scroller.clientWidth / 2),
        behavior: "smooth"
      });
    }
  }

  scrollToCategory(event) {
    event.preventDefault();
    const categoryId = event.currentTarget.dataset.categoryId;
    const target = document.getElementById(categoryId);
    if (!target) return;
    // Filtered out by the active search: scrolling would go nowhere and the highlight would stick.
    if (event.currentTarget.classList.contains("disabled") || target.style.display === "none") return;

    this.scrollLockUntil = Date.now() + 800;
    this.activeCategoryId = null;
    this.setActiveCategory(categoryId);

    const headerOffset = this.hasStickyHeaderTarget
      ? this.stickyHeaderTarget.offsetHeight + 12
      : 180;

    const elementPosition = target.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });

    // If the click came from inside a dropdown, close the dropdown
    const dropdownToggle = event.currentTarget.closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]');
    if (dropdownToggle) {
      const dropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
      if (dropdown) dropdown.hide();
    }

    // If the click came from inside a modal, close the modal
    const modalElement = event.currentTarget.closest('.modal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
  }

  // --- Live Search & Filtering ---
  liveSearch(event) {
    const searchValue = event.target.value.toLowerCase();
    const filters = this.getFilters();
    this.toggleSearchClear(event.target.value.length > 0);
    this.applyAllFilters(searchValue, filters);

    this.scheduleSearchTrack(event.target);
  }

  // One debounce timer per input: the nav, sidebar, and sheet search boxes
  // coexist, and a shared timer would let activity in one cancel another's
  // pending track.
  scheduleSearchTrack(input) {
    this.searchTrackTimers ||= new Map();
    clearTimeout(this.searchTrackTimers.get(input));
    this.searchTrackTimers.set(input, setTimeout(() => {
      if (input.value.trim().length >= 2) {
        trackUxEvent("interaction", "search", "menu_search");
      }
    }, 1200));
  }

  clearSearch() {
    const inputs = document.querySelectorAll(".js-menu-search");
    inputs.forEach((input) => (input.value = ""));
    if (inputs[0]) inputs[0].focus();
    if (this.hasMealSearchTarget) this.mealSearchTarget.value = "";
    this.toggleSearchClear(false);
    this.applyAllFilters("", this.getFilters());
  }

  // --- Mobile search sheet ---
  // Results render as rows inside the sheet; the page behind stays unfiltered, so there is
  // no page state to restore when the sheet closes — just the sheet's own input.
  focusSheetSearch() {
    if (this.hasSheetSearchInputTarget) this.sheetSearchInputTarget.focus();
  }

  clearSheetSearch() {
    if (!this.hasSheetSearchInputTarget) return;

    this.sheetSearchInputTarget.value = "";
    this.renderSheetResults("");
  }

  sheetSearch(event) {
    this.renderSheetResults(event.target.value.trim().toLowerCase());
    this.scheduleSearchTrack(event.target);
  }

  renderSheetResults(query) {
    if (!this.hasSheetSearchResultsTarget) return;

    this.sheetSearchResultsTarget.replaceChildren();

    if (query.length === 0) {
      if (this.hasSheetSearchCountTarget) this.sheetSearchCountTarget.classList.add("d-none");
      if (this.hasSheetSearchEmptyTarget) {
        this.sheetSearchEmptyTarget.textContent = "Search the menu by name or ingredient.";
        this.sheetSearchEmptyTarget.classList.remove("d-none");
      }
    } else {
      // "Most Ordered" repeats items that also live in their own menu section, so the card
      // list can hold the same item twice — de-dupe by item id before rendering rows.
      const filters = this.getFilters();
      const seen = new Set();
      const matches = Array.from(
        document.querySelectorAll('[data-online-orders-filter-target="mealItem"]')
      ).filter((card) => {
        if (seen.has(card.dataset.itemId)) return false;
        if (!this.matchesFilters(query, filters, this.getMealAttributes(card))) return false;

        seen.add(card.dataset.itemId);
        return true;
      });

      matches.forEach((card) => this.sheetSearchResultsTarget.appendChild(this.buildSheetResultRow(card)));

      if (this.hasSheetSearchCountTarget) {
        this.sheetSearchCountTarget.textContent = `${matches.length} ${matches.length === 1 ? "result" : "results"}`;
        this.sheetSearchCountTarget.classList.toggle("d-none", matches.length === 0);
      }
      if (this.hasSheetSearchEmptyTarget) {
        this.sheetSearchEmptyTarget.textContent = `No matches for “${query}”`;
        this.sheetSearchEmptyTarget.classList.toggle("d-none", matches.length > 0);
      }
    }

    const scroller = this.sheetSearchResultsTarget.closest(".js-sheet-fade");
    if (scroller) this.updateSheetFade(scroller);
  }

  // Rows are built as DOM nodes (not innerHTML) so item names can't inject markup.
  buildSheetResultRow(card) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "oc-search-result";
    row.setAttribute("data-track", "search.sheet_result_opened");
    row.addEventListener("click", () => this.openSheetResult(card));

    const thumbSrc = card.querySelector(".oc-item-card__media img")?.src;
    if (thumbSrc) {
      const thumb = document.createElement("img");
      thumb.src = thumbSrc;
      thumb.alt = "";
      thumb.loading = "lazy";
      thumb.className = "oc-search-result__thumb";
      row.appendChild(thumb);
    }

    const name = document.createElement("span");
    name.className = "oc-search-result__name";
    name.textContent = card.dataset.name;
    row.appendChild(name);

    const priceText = card.querySelector(".menu-item-price small")?.textContent.trim();
    if (priceText) {
      const price = document.createElement("span");
      price.className = "oc-search-result__price";
      price.textContent = priceText;
      row.appendChild(price);
    }

    return row;
  }

  openSheetResult(card) {
    // Bootstrap strips the `show` class synchronously in hide(), so the class
    // alone can't gate a second tap landing during the dismiss fade — without
    // this flag two taps race their remote requests into the item modal.
    if (this.sheetResultPending) return;

    // Reuse the existing remote link → newModal flow, but only after the sheet has finished
    // hiding — opening the item modal mid-dismiss leaves Bootstrap's backdrops fighting.
    const openItem = () => {
      this.sheetResultPending = false;
      card.querySelector('a[data-remote="true"]')?.click();
    };
    const sheet = document.getElementById("searchSheet");
    if (sheet && sheet.classList.contains("show")) {
      this.sheetResultPending = true;
      sheet.addEventListener("hidden.bs.modal", openItem, { once: true });
      bootstrap.Modal.getOrCreateInstance(sheet).hide();
    } else {
      openItem();
    }
  }

  applyFilters(event) {
    event.preventDefault();
    const searchValue = this.hasMealSearchTarget ? this.mealSearchTarget.value.toLowerCase() : "";
    const filters = this.getFilters();
    this.applyAllFilters(searchValue, filters);
    this.closeModal();
  }

  clearFilters(event) {
    if (this.hasMealSearchTarget) this.mealSearchTarget.value = "";
    document.querySelectorAll(".js-menu-search").forEach((input) => (input.value = ""));

    if (this.hasTopItemFilterTarget) this.topItemFilterTarget.checked = false;
    if (this.hasHouseSpecialFilterTarget) this.houseSpecialFilterTarget.checked = false;
    if (this.hasGlutenFreeFilterTarget) this.glutenFreeFilterTarget.checked = false;
    if (this.hasVegetarianFilterTarget) this.vegetarianFilterTarget.checked = false;
    if (this.hasVeganFilterTarget) this.veganFilterTarget.checked = false;

    this.toggleSearchClear(false);
    this.applyAllFilters("", this.getFilters());
    this.closeModal();
  }

  applyAllFilters(searchValue, filters) {
    const mealItems = document.querySelectorAll('[data-online-orders-filter-target="mealItem"]');
    let visibleCount = 0;
    mealItems.forEach((mealItem) => {
      const mealAttributes = this.getMealAttributes(mealItem);
      const matches = this.matchesFilters(searchValue, filters, mealAttributes);
      mealItem.style.display = matches ? "" : "none";
      if (matches) visibleCount += 1;
    });

    this.updateFilterCount(filters, searchValue);
    this.hideEmptySchedules();
    this.syncCategoryNavAvailability();
    this.toggleNoResults(visibleCount === 0);
  }

  // The category nav is rendered once server-side and knows nothing about the active search, so
  // links to filtered-out sections stayed clickable and did nothing — worst on a zero-result search,
  // where every link was dead but still highlighted on click. Mirrors what hideEmptySchedules just
  // did to the sections themselves. clearSearch/clearFilters both re-enter applyAllFilters, so
  // re-enabling comes for free.
  syncCategoryNavAvailability() {
    const links = document.querySelectorAll(
      ".oc-category-pill, .oc-modal-category-link, .oc-dropdown-category-link, .oc-sidebar-category-link"
    );

    links.forEach((link) => {
      const section = document.getElementById(link.dataset.categoryId);
      const hidden = !!section && section.style.display === "none";

      link.classList.toggle("disabled", hidden);
      link.setAttribute("aria-disabled", hidden ? "true" : "false");

      // A category filtered out from under an active highlight would otherwise keep it until the
      // next real scroll.
      if (hidden && link.classList.contains("active")) {
        link.classList.remove("active");
        if (this.activeCategoryId === link.dataset.categoryId) this.activeCategoryId = null;
        // The underline is only ever moved by setActiveCategory, and on a zero-result search no
        // scroll can re-run it — so collapse it here rather than leave it drawn under a dimmed,
        // unclickable pill. updateCategoryUnderline re-sizes it on the next active category.
        if (link.classList.contains("oc-category-pill") && this.hasCategoryUnderlineTarget) {
          this.categoryUnderlineTarget.style.width = "0px";
        }
      }
    });
  }

  toggleNoResults(show) {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("d-none", !show);
    }
  }

  toggleSearchClear(hasValue) {
    if (this.hasSearchClearTarget) {
      this.searchClearTargets.forEach(target => {
        target.classList.toggle("d-none", !hasValue);
      });
    }
  }

  getFilters() {
    return {
      glutenFree: this.hasGlutenFreeFilterTarget ? this.glutenFreeFilterTarget.checked : false,
      vegetarian: this.hasVegetarianFilterTarget ? this.vegetarianFilterTarget.checked : false,
      vegan: this.hasVeganFilterTarget ? this.veganFilterTarget.checked : false,
      houseSpecial: this.hasHouseSpecialFilterTarget ? this.houseSpecialFilterTarget.checked : false,
      topItem: this.hasTopItemFilterTarget ? this.topItemFilterTarget.checked : false
    };
  }

  getMealAttributes(mealItem) {
    return {
      name: mealItem.getAttribute("data-name").toLowerCase(),
      description: mealItem.getAttribute("data-description")?.toLowerCase() || "",
      glutenFree: mealItem.getAttribute("data-gluten-free") === "true",
      vegetarian: mealItem.getAttribute("data-vegetarian") === "true",
      vegan: mealItem.getAttribute("data-vegan") === "true",
      houseSpecial: mealItem.getAttribute("data-house-special") === "true",
      topItem: mealItem.getAttribute("data-top-item") === "true"
    };
  }

  matchesFilters(searchValue, filters, mealAttributes) {
    const matchesSearch =
      mealAttributes.name.includes(searchValue) ||
      mealAttributes.description.includes(searchValue) ||
      searchValue === "";
    const matchesGlutenFree = !filters.glutenFree || mealAttributes.glutenFree;
    const matchesVegetarian = !filters.vegetarian || mealAttributes.vegetarian;
    const matchesVegan = !filters.vegan || mealAttributes.vegan;
    const matchesHouseSpecial = !filters.houseSpecial || mealAttributes.houseSpecial;
    const matchesTopItem = !filters.topItem || mealAttributes.topItem;

    return (
      matchesSearch &&
      matchesGlutenFree &&
      matchesVegetarian &&
      matchesVegan &&
      matchesHouseSpecial &&
      matchesTopItem
    );
  }

  updateFilterCount(filters, searchValue) {
    const filterCountElements = document.querySelectorAll(".oc-filter-count");
    if (filterCountElements.length === 0) return;

    const filterCount =
      (filters.glutenFree ? 1 : 0) +
      (filters.vegetarian ? 1 : 0) +
      (filters.vegan ? 1 : 0) +
      (filters.houseSpecial ? 1 : 0) +
      (filters.topItem ? 1 : 0) +
      (searchValue ? 1 : 0);

    filterCountElements.forEach((element) => {
      if (filterCount > 0) {
        element.classList.remove("d-none");
        element.textContent = filterCount;
      } else {
        element.classList.add("d-none");
        element.textContent = "";
      }
    });
  }

  closeModal() {
    const modal = document.getElementById("filterModal");
    if (modal) {
      const bootstrapModal = bootstrap.Modal.getOrCreateInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
  }

  hideEmptySchedules() {
    this.hideEmptyContainers(".oc-menu-schedule-section");
    this.hideEmptyContainers(".oc-category-section");
  }

  hideEmptyContainers(selector) {
    const containers = document.querySelectorAll(selector);
    containers.forEach((container) => {
      const mealItems = container.querySelectorAll('[data-online-orders-filter-target="mealItem"]');
      if (mealItems.length === 0) return;
      const hasVisibleItems = Array.from(mealItems).some(
        (item) => item.style.display !== "none"
      );
      container.style.display = hasVisibleItems ? "block" : "none";
    });
  }

  // --- Cart & Stepper Actions (from layout B) ---
  // The server re-renders the whole of #cart-offcanvas, but only two things inside it actually
  // vary: the flash stack and #cart. Everything else — the drag handle, the close button, the
  // "Order Summary" header, the offcanvas element's own class list — is static markup.
  //
  // So patch those two in place and never replace the container. #offcanvasRight has to survive:
  // Bootstrap holds a reference to it and may be mid-transition, and replacing it during the
  // 300ms show/hide left Bootstrap animating a detached node — a backdrop over a page with no
  // cart. Merging unconditionally means that state is unreachable rather than special-cased.
  updateCart(event) {
    const [_data, _status, xhr] = event.detail;

    const cartOffcanvas = document.getElementById("cart-offcanvas");
    if (!cartOffcanvas) return;

    const incoming = document.createElement("div");
    incoming.innerHTML = xhr.response;

    const newFlash = incoming.querySelector(".oc-flash-stack");
    const oldFlash = cartOffcanvas.querySelector(".oc-flash-stack");
    if (newFlash && oldFlash) {
      oldFlash.replaceWith(newFlash);
    } else if (newFlash) {
      // First cart update of the page: is_page_load renders no stack, so there's none to swap.
      cartOffcanvas.prepend(newFlash);
    }

    const newCart = incoming.querySelector("#cart");
    const oldCart = cartOffcanvas.querySelector("#cart");
    if (newCart && oldCart) {
      // data-cart-qty sits on #cart itself, not inside it, so the innerHTML merge below would
      // leave it at whatever the page first loaded with and updateCartQty would read a stale
      // count for the rest of the session. Copy it across before replacing the contents.
      oldCart.dataset.cartQty = newCart.dataset.cartQty || "0";
      oldCart.innerHTML = newCart.innerHTML;
    }

    if (event.target.tagName === "FORM") {
      this.resetForm(event.target);
    }
    this.updateCartQty();
    // The innerHTML merge replaced the cart scroller (.cart--inner.js-sheet-fade),
    // so its fade state must be recomputed for the fresh content height.
    this.refreshSheetFades();
    queueMicrotask(() => {
      this.clearScrollLockStyles();
      this.initFlashes();
    });
  }

  newModal(event) {
    const [_data, _status, xhr] = event.detail;
    const offcanvasElement = document.getElementById("offcanvasRight");
    const openModal = document.getElementById("itemModal");
    // Remember whether this modal was opened from the cart so closing it can put the cart back.
    // A size chip re-enters newModal while the modal is already open and the offcanvas has long
    // since been hidden, so in that case inherit the flag instead of re-reading the offcanvas.
    const reopenCart = openModal?.classList.contains("show")
      ? openModal.dataset.reopenCart === "true"
      : !!offcanvasElement?.classList.contains("show");

    if (offcanvasElement) {
      const offcanvas = bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (offcanvas) {
        offcanvas.hide();
      }
    }

    const newModalContent = document.getElementById("itemModalContent");
    if (newModalContent) {
      newModalContent.innerHTML = xhr.response;
      const newModal = document.getElementById("itemModal");
      if (newModal) {
        newModal.dataset.reopenCart = reopenCart ? "true" : "false";
        const modal = bootstrap.Modal.getOrCreateInstance(newModal);
        modal.show();
        this.checkboxLimit();
        scanImpressions(newModal);
        queueMicrotask(() => this.clearScrollLockStyles());
        requestAnimationFrame(() => this.clearScrollLockStyles());
      }
    }
  }

  updateMealPrice(event) {
    this.enforceSingleChoice(event);
    this.updateItemPrice();
    this.checkboxLimit();
  }

  // Choose-1 groups behave like radios: picking another option swaps the selection instead
  // of forcing an uncheck-first round trip. Multi-choice groups keep disable-at-limit.
  enforceSingleChoice(event) {
    const changed = event?.target;
    if (!changed?.checked || parseInt(changed.dataset.max, 10) !== 1) return;

    document
      .querySelectorAll(`.modifier-checkbox[data-group="${changed.dataset.group}"]`)
      .forEach((checkbox) => {
        if (checkbox !== changed) checkbox.checked = false;
      });
  }

  decreaseValue() {
    const numberInput = this.orderQtyTarget;
    let value = parseInt(numberInput.value, 10);
    numberInput.value = isNaN(value) || value <= 1 ? 1 : value - 1;
    this.updateItemPrice();
  }

  increaseValue() {
    const numberInput = this.orderQtyTarget;
    let value = parseInt(numberInput.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    numberInput.value = value + 1;
    this.updateItemPrice();
  }

  // Per-unit price = base (server-rendered for the selected size, stored on the
  // itemPrice target's data-price) + the sum of checked modifiers. Recomputing
  // from scratch keeps the displayed total correct regardless of how quantity
  // changed (stepper buttons, typing, or the native number-input spinner) and
  // avoids the stale-divisor NaN the previous quantity-ratio math could produce.
  modifierTotal() {
    const form = this.orderQtyTarget.closest("form");
    if (!form) return 0;

    let sum = 0;
    form.querySelectorAll(".modifier-checkbox:checked").forEach((checkbox) => {
      const raw = checkbox.dataset.modifierPrice;
      if (raw) sum += parseFloat(raw.replace(/[$,]/g, "")) || 0;
    });
    return sum;
  }

  unitPrice() {
    const base =
      parseFloat(String(this.itemPriceTarget.dataset.price || "").replace(/[$,]/g, "")) || 0;
    return base + this.modifierTotal();
  }

  updateItemPrice() {
    if (!this.hasItemPriceTarget || !this.hasOrderQtyTarget) return;

    const quantity = parseInt(this.orderQtyTarget.value, 10);
    if (isNaN(quantity) || quantity < 1) return;

    const total = this.unitPrice() * quantity;
    this.itemPriceTarget.innerHTML = "$" + total.toFixed(2);
  }

  // The order-type switch is a session write behind CSRF. When the token has gone stale — a
  // bfcache restore, a tab left open past session rotation, a cached page — the POST 422s and,
  // before this, nothing happened at all: the sheet closed, the header kept the old type, and the
  // customer clicked again. Surface it and reload, which is what actually recovers the token.
  handleOrderTypeAjaxError() {
    if (this.orderTypeErrorHandled) return; // a double-click must not stack toasts or reloads
    this.orderTypeErrorHandled = true;

    this.showErrorToast("We couldn't update your order type. Refreshing the page…");

    // Tracked so disconnect() can cancel it — otherwise a Turbolinks navigation inside the
    // 1.5s window reloads whatever page the customer just landed on.
    this.orderTypeReloadTimer = setTimeout(() => window.location.reload(), 1500);
  }

  // Mirrors _oc_flash.html.erb so the toast inherits that partial's CSS, enter/exit animation and
  // oc-flash#dismiss wiring — Stimulus connects the new data-controller via its own observer. Keep
  // the two in sync if the partial's class names change.
  showErrorToast(message) {
    // _flash_messages.html.erb returns early when there are no server flashes, so on most
    // pages the stack doesn't exist yet.
    let stack = document.querySelector(".oc-flash-stack--page");
    if (!stack) {
      stack = document.createElement("div");
      stack.className = "oc-flash-stack oc-flash-stack--page";
      stack.setAttribute("aria-live", "polite");
      stack.setAttribute("aria-relevant", "additions");
      document.body.appendChild(stack);
    }

    const toast = document.createElement("div");
    toast.className = "toast oc-flash oc-flash--error";
    toast.setAttribute("role", "alert");
    toast.setAttribute("aria-live", "polite");
    toast.setAttribute("aria-atomic", "true");
    toast.dataset.controller = "oc-flash";
    // Only the static chrome goes through innerHTML; the message is set as text below, so a
    // future caller passing a server string can't inject markup into a public ordering page.
    toast.innerHTML = `
      <span class="oc-flash__icon" aria-hidden="true"><i class="bi bi-exclamation-lg"></i></span>
      <div class="oc-flash__body"><p class="oc-flash__message mb-0"></p></div>
      <button type="button" class="oc-flash__close" data-action="click->oc-flash#dismiss"
              aria-label="Dismiss"><i class="bi bi-x-lg" aria-hidden="true"></i></button>`;
    toast.querySelector(".oc-flash__message").textContent = message;
    stack.appendChild(toast);

    // Added on the next frame so the enter animation runs rather than the toast appearing already
    // in its final state.
    requestAnimationFrame(() => toast.classList.add("show"));
  }

  // Closing an item modal that was opened from the cart puts the cart back, instead of dropping
  // the customer on the menu with their place lost. Covers both cancelling and a successful
  // "Update Item" — neither reopened the offcanvas before.
  handleItemModalHidden(event) {
    // currentTarget, not target: hidden.bs.modal bubbles, and only the modal this is bound to
    // carries the flag.
    const modal = event.currentTarget;
    if (modal.dataset.reopenCart !== "true") return;

    modal.dataset.reopenCart = "false";
    this.openOffcanvas();
  }

  openOffcanvas() {
    const offcanvasElement = document.getElementById("offcanvasRight");
    if (!offcanvasElement) return;

    let offcanvasInstance = bootstrap.Offcanvas.getInstance(offcanvasElement);
    if (!offcanvasInstance) {
      offcanvasInstance = new bootstrap.Offcanvas(offcanvasElement);
    }
    offcanvasInstance.show();
  }

  closeOffcanvas() {
    const offcanvasElement = document.getElementById("offcanvasRight");
    if (offcanvasElement) {
      const offcanvas = bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (offcanvas) {
        offcanvas.hide();
      }
    }
  }

  // --- Bottom-sheet drag to dismiss (mobile only) ---
  isMobileSheet() {
    return window.matchMedia("(max-width: 767.98px)").matches;
  }

  sheetElementFor(handle) {
    const kind = handle.dataset.sheetKind;
    return kind === "offcanvas"
      ? handle.closest(".offcanvas")
      : handle.closest(".modal-dialog");
  }

  sheetDragStart(event) {
    if (!this.isMobileSheet()) return;
    const handle = event.currentTarget;
    this.draggedSheet = this.sheetElementFor(handle);
    this.draggedSheetKind = handle.dataset.sheetKind;
    if (!this.draggedSheet) return;

    this.sheetStartY = event.touches[0].clientY;
    this.sheetLastY = this.sheetStartY;
    this.sheetDelta = 0;
    this.draggedSheet.style.transition = "none";
  }

  sheetDragMove(event) {
    if (!this.draggedSheet) return;
    const currentY = event.touches[0].clientY;
    this.sheetLastY = currentY;
    const delta = currentY - this.sheetStartY;
    if (delta <= 0) {
      this.sheetDelta = 0;
      this.draggedSheet.style.transform = "";
      return;
    }
    this.sheetDelta = delta;
    this.draggedSheet.style.transform = `translateY(${delta}px)`;
  }

  sheetDragEnd() {
    if (!this.draggedSheet) return;
    this.settleSheet(this.draggedSheet, this.draggedSheetKind, this.sheetDelta);
    this.draggedSheet = null;
    this.draggedSheetKind = null;
    this.sheetDelta = 0;
  }

  // Shared end-of-drag decision used by both the top handle and the
  // pull-down-from-scroll-top gesture: past threshold → dismiss, else spring back.
  settleSheet(sheet, kind, delta) {
    const dismissed = delta > Math.min(sheet.offsetHeight * 0.25, 160);

    // Restore the CSS-driven transition so both spring-back and dismiss animate.
    sheet.style.transition = "";

    if (dismissed) {
      // Continue the downward motion (no spring-up), and fade the backdrop in
      // sync by triggering the Bootstrap hide at the same time.
      sheet.style.transform = "translateY(100%)";
      if (kind === "offcanvas") {
        this.closeOffcanvas();
      } else {
        const modal = sheet.closest(".modal");
        if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide();
      }
      const cleanup = () => {
        sheet.style.transform = "";
        sheet.removeEventListener("transitionend", cleanup);
      };
      sheet.addEventListener("transitionend", cleanup, { once: true });
      setTimeout(cleanup, 450);
    } else {
      // Spring back to the open position.
      sheet.style.transform = "";
    }
  }

  // --- Pull-down-from-scroll-top to dismiss (mobile only) ---
  // When a sheet's scrollable body is already at the top and the user drags
  // down, take over the gesture and drive the sheet collapse instead of letting
  // the browser rubber-band overscroll.
  initSheetScrollDrag() {
    this.sheetScrollers = Array.from(
      this.element.querySelectorAll(".js-sheet-scroll")
    );
    if (!this.sheetScrollers.length) return;

    this.onSheetScrollStart = this.sheetScrollDragStart.bind(this);
    this.onSheetScrollMove = this.sheetScrollDragMove.bind(this);
    this.onSheetScrollEnd = this.sheetScrollDragEnd.bind(this);

    this.sheetScrollers.forEach((el) => {
      el.addEventListener("touchstart", this.onSheetScrollStart, { passive: true });
      el.addEventListener("touchmove", this.onSheetScrollMove, { passive: false });
      el.addEventListener("touchend", this.onSheetScrollEnd, { passive: true });
    });
  }

  destroySheetScrollDrag() {
    if (!this.sheetScrollers) return;
    this.sheetScrollers.forEach((el) => {
      el.removeEventListener("touchstart", this.onSheetScrollStart);
      el.removeEventListener("touchmove", this.onSheetScrollMove);
      el.removeEventListener("touchend", this.onSheetScrollEnd);
    });
    this.sheetScrollers = null;
  }

  // --- Background scroll guard (mobile, sheets only) ---
  // The body overflow lock doesn't stop iOS touch scrolling: a touch on a
  // non-scrollable part of an open sheet chains to the page behind it. Block
  // any touchmove that no scroller inside the sheet can consume; real scroll
  // regions keep native scrolling, and their overscroll-behavior: contain
  // stops boundary chaining. The cart offcanvas gets the same guard, bound once
  // by bindOffcanvasElement — updateCart patches that element rather than
  // replacing it, so the listener never needs re-attaching.
  initModalScrollGuard() {
    this.onModalTouchMove = (event) => {
      if (!this.isMobileSheet()) return;
      const container = event.currentTarget;
      const content = container.classList.contains("offcanvas")
        ? container
        : container.querySelector(".modal-content");
      const scroller = content && content.contains(event.target)
        ? this.scrollableFrom(event.target, content)
        : null;
      if (!scroller) event.preventDefault();
    };

    this.modalScrollGuardEls = Array.from(this.element.querySelectorAll(".modal"));
    this.modalScrollGuardEls.forEach((el) => {
      el.addEventListener("touchmove", this.onModalTouchMove, { passive: false });
    });

    // The offcanvas was bound before this handler existed — attach it now.
    if (this.offcanvasElement) {
      this.offcanvasElement.addEventListener("touchmove", this.onModalTouchMove, { passive: false });
    }
  }

  destroyModalScrollGuard() {
    if (!this.onModalTouchMove) return;
    (this.modalScrollGuardEls || []).forEach((el) => {
      el.removeEventListener("touchmove", this.onModalTouchMove);
    });
    if (this.offcanvasElement) {
      this.offcanvasElement.removeEventListener("touchmove", this.onModalTouchMove);
    }
    this.modalScrollGuardEls = null;
    this.onModalTouchMove = null;
  }

  // Nearest actually-scrollable element between the touched node and the sheet
  // container (the `.js-sheet-scroll` element may itself be a non-scrolling
  // flex wrapper whose child does the scrolling).
  scrollableFrom(node, container) {
    let el = node;
    const stop = container.parentElement;
    while (el && el !== stop) {
      if (el.scrollHeight > el.clientHeight) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return el;
      }
      el = el.parentElement;
    }
    return container.scrollHeight > container.clientHeight ? container : null;
  }

  sheetScrollDragStart(event) {
    if (!this.isMobileSheet()) return;
    const container = event.currentTarget;
    this.bodyDragKind = container.dataset.sheetKind;
    this.bodyDragSheet =
      this.bodyDragKind === "offcanvas"
        ? container.closest(".offcanvas")
        : container.closest(".modal-dialog");
    this.bodyDragScrollEl = this.scrollableFrom(event.target, container);
    // Gate on the position at gesture START. If the user begins mid-content,
    // this gesture never collapses — even if they later reach the top.
    this.bodyDragAtTop =
      !this.bodyDragScrollEl || this.bodyDragScrollEl.scrollTop <= 0;
    this.bodyDragStartY = event.touches[0].clientY;
    this.bodyDragDelta = 0;
    this.bodyDragEngaged = false;
  }

  sheetScrollDragMove(event) {
    if (!this.bodyDragSheet) return;
    const delta = event.touches[0].clientY - this.bodyDragStartY;

    if (!this.bodyDragEngaged) {
      // Take over only when the gesture began at the top, the scroller is still
      // at the top, and the pull is downward (an overscroll). Otherwise let the
      // body scroll as normal.
      const atTopNow =
        !this.bodyDragScrollEl || this.bodyDragScrollEl.scrollTop <= 0;
      if (this.bodyDragAtTop && atTopNow && delta > 0) {
        this.bodyDragEngaged = true;
        this.bodyDragSheet.style.transition = "none";
      } else {
        return;
      }
    }

    // User reversed upward mid-gesture — release back to native scroll.
    if (delta <= 0) {
      this.bodyDragDelta = 0;
      this.bodyDragSheet.style.transform = "";
      this.bodyDragSheet.style.transition = "";
      this.bodyDragEngaged = false;
      return;
    }

    event.preventDefault();
    this.bodyDragDelta = delta;
    this.bodyDragSheet.style.transform = `translateY(${delta}px)`;
  }

  sheetScrollDragEnd() {
    if (!this.bodyDragSheet) return;
    if (this.bodyDragEngaged) {
      this.settleSheet(this.bodyDragSheet, this.bodyDragKind, this.bodyDragDelta);
    } else {
      this.bodyDragSheet.style.transition = "";
      this.bodyDragSheet.style.transform = "";
    }
    this.bodyDragSheet = null;
    this.bodyDragScrollEl = null;
    this.bodyDragAtTop = false;
    this.bodyDragKind = null;
    this.bodyDragDelta = 0;
    this.bodyDragEngaged = false;
  }

  // Cart toasts only. Everything else auto-dismisses via data-oc-autohide-ms, which oc-flash
  // owns — that controller connects on every layout C page, including the checkout pages this
  // one isn't mounted on.
  toastTimeout() {
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      document.querySelectorAll(".toast-success.oc-flash").forEach((toast) => this.fadeOutToast(toast));
    }, 1500);
  }

  fadeOutToast(toast) {
    if (!toast.isConnected) return;

    toast.classList.add("fade-out");
    toast.addEventListener("animationend", (e) => {
      if (e.target !== toast || e.animationName !== "oc-flash-exit") return;
      toast.remove();
    }, { once: true });
    // Fallback for reduced-motion / interrupted animations, matching oc-flash#dismiss: without
    // it an animation that never completes strands the toast on screen with .fade-out set,
    // which also makes a later dismiss() return early at its guard.
    setTimeout(() => toast.remove(), 400);
  }

  // No bootstrap.Toast instances: layout C toasts are rendered with .show in the markup and
  // dismissed by CSS animation (oc-flash / fadeOutToast), so an instance was never used for
  // anything — it only leaked an entry in Bootstrap's element Data map, since these get
  // .remove()d without .dispose().
  initFlashes() {
    this.toastTimeout();
  }

  resetForm(formElement) {
    formElement.reset();
    const checkboxes = formElement.querySelectorAll("input[type='checkbox']");
    checkboxes.forEach((checkbox) => (checkbox.disabled = false));
    const submitInput = formElement.querySelector("input[type='submit']");
    if (submitInput) submitInput.classList.add("disabled");
    const price = formElement.querySelector(".item-price");
    if (price) {
      price.innerHTML = price.dataset.price;
    }
  }

  // The nav badge and the mobile cart bar sit outside the offcanvas the server re-renders, so
  // they're synced here from #cart's data-cart-qty rather than by re-parsing the quantity text
  // of every cart row — that count also decides whether the cart bar is visible at all.
  updateCartQty() {
    const cartQty = parseInt(document.getElementById("cart")?.dataset.cartQty, 10) || 0;

    const cartQtyBadge = document.getElementById("cartQty");
    if (cartQtyBadge) {
      cartQtyBadge.textContent = cartQty;
    }

    const cartQtyMobile = document.getElementById("cartQtyMobile");
    if (cartQtyMobile) {
      cartQtyMobile.textContent = cartQty;
    }
  }

  checkboxLimit() {
    const checkboxes = Array.from(
      document.querySelectorAll(".modifier-checkbox")
    );

    if (checkboxes.length === 0) {
      this.updateBtnState(true);
      return;
    }

    let allRequirementsMet = true;
    let groups = new Set(checkboxes.map((checkbox) => checkbox.dataset.group));

    groups.forEach((group) => {
      let groupCheckboxes = checkboxes.filter(
        (checkbox) => checkbox.dataset.group === group
      );
      let max = parseInt(groupCheckboxes[0].dataset.max);
      let min = parseInt(groupCheckboxes[0].dataset.min);
      let checkedCount = groupCheckboxes.filter((cb) => cb.checked).length;
      let unchecked = groupCheckboxes.filter((cb) => !cb.checked);
      
      // max 1 swaps via enforceSingleChoice instead of disabling the other options.
      let disable = checkedCount >= max && max > 1;
      unchecked.forEach((cb) => {
        cb.disabled = disable;
        cb.closest(".oc-modifier-row")?.classList.toggle("oc-modifier-row--disabled", disable);
      });

      if (checkedCount < min && min > 0) {
        allRequirementsMet = false;
      }
    });
    this.updateBtnState(allRequirementsMet);
  }

  checkSuggestions(event) {
    const button = event.currentTarget;
    const threshold = parseInt(button.dataset.threshold, 10);
    const total = parseInt(button.dataset.currentTotal, 10);
    const suggestionsEnabled = button.dataset.suggestionsEnabled === "true";
    const checkoutUrl = button.dataset.checkoutUrl;
    const suggestionItemLimit = button.dataset.suggestionItemLimit
      ? parseInt(button.dataset.suggestionItemLimit, 10)
      : null;
    const suggestionItemCount = parseInt(button.dataset.suggestionItemCount, 10) || 0;

    const atSuggestionLimit =
      suggestionItemLimit != null && suggestionItemCount >= suggestionItemLimit;

    if (atSuggestionLimit) {
      trackUxEvent("blocked", "suggestions", "suggestion_limit_reached", {
        reason: "suggestion_limit",
      });
    }

    if (
      suggestionsEnabled &&
      threshold > 0 &&
      total < threshold &&
      !atSuggestionLimit
    ) {
      sessionStorage.setItem("suggestionsCheckoutUrl", checkoutUrl);
      const suggestionsModal = document.getElementById("suggestionsModal");
      if (suggestionsModal) {
        bootstrap.Modal.getOrCreateInstance(suggestionsModal).show();
      }
    } else {
      // Navigating straight to checkout — show a spinner and lock the button so
      // the tap registers and can't be repeated while the page loads.
      button.classList.add("is-loading", "disabled");
      window.location.href = checkoutUrl;
    }
  }

  openSuggestionItem(event) {
    const suggestionsModal = bootstrap.Modal.getInstance(
      document.getElementById("suggestionsModal")
    );
    if (suggestionsModal) suggestionsModal.hide();
    this.newModal(event);
  }

  continueToCheckout() {
    const checkoutUrl = sessionStorage.getItem("suggestionsCheckoutUrl");
    if (checkoutUrl) window.location.href = checkoutUrl;
  }

  updateBtnState(requirements_met) {
    const cartBtn = document.getElementById("btnCart");
    if (!cartBtn) {
      return;
    }
    if (requirements_met) {
      cartBtn.classList.remove("disabled");
    } else {
      cartBtn.classList.add("disabled");
    }
  }
}
